import { DateTime } from 'luxon';
import { Weather, WeatherSymbol } from './weather';

// Docs: https://opendata.smhi.se/metfcst/snow1gv1
export const smhiEndpoint =
  'https://opendata-download-metfcst.smhi.se/api/category/snow1g/version/1/geotype/point/lon/{lon}/lat/{lat}/data.json';

export interface WeatherForecast {
  createdTime: string;
  referenceTime: string;
  geometry: Geometry;
  timeSeries: TimeSeries[];
}

export interface Geometry {
  type: string;
  coordinates: number[];
}

export interface TimeSeries {
  time: string;
  intervalParametersStartTime?: string;
  data: ForecastData;
}

export interface ForecastData {
  air_temperature?: number;
  wind_from_direction?: number;
  wind_speed?: number;
  wind_speed_of_gust?: number;
  relative_humidity?: number;
  air_pressure_at_mean_sea_level?: number;
  visibility_in_air?: number;
  thunderstorm_probability?: number;
  probability_of_frozen_precipitation?: number;
  cloud_area_fraction?: number;
  low_type_cloud_area_fraction?: number;
  medium_type_cloud_area_fraction?: number;
  high_type_cloud_area_fraction?: number;
  cloud_base_altitude?: number;
  cloud_top_altitude?: number;
  precipitation_amount_mean_deterministic?: number;
  precipitation_amount_mean?: number;
  precipitation_amount_min?: number;
  precipitation_amount_max?: number;
  precipitation_amount_median?: number;
  probability_of_precipitation?: number;
  precipitation_frozen_part?: number;
  predominant_precipitation_type_at_surface?: number;
  symbol_code?: number;
}

export const parseWeatherSymbol = (value: number): WeatherSymbol => {
  switch (value) {
    case 1:
      return 'Clear sky';
    case 2:
      return 'Nearly clear sky';
    case 3:
      return 'Variable cloudiness';
    case 4:
      return 'Halfclear sky';
    case 5:
      return 'Cloudy sky';
    case 6:
      return 'Overcast';
    case 7:
      return 'Fog';
    case 8:
      return 'Light rain showers';
    case 9:
      return 'Moderate rain showers';
    case 10:
      return 'Heavy rain showers';
    case 11:
      return 'Thunderstorm';
    case 12:
      return 'Light sleet showers';
    case 13:
      return 'Moderate sleet showers';
    case 14:
      return 'Heavy sleet showers';
    case 15:
      return 'Light snow showers';
    case 16:
      return 'Moderate snow showers';
    case 17:
      return 'Heavy snow showers';
    case 18:
      return 'Light rain';
    case 19:
      return 'Moderate rain';
    case 20:
      return 'Heavy rain';
    case 21:
      return 'Thunder';
    case 22:
      return 'Light sleet';
    case 23:
      return 'Moderate sleet';
    case 24:
      return 'Heavy sleet';
    case 25:
      return 'Light snowfall';
    case 26:
      return 'Moderate snowfall';
    case 27:
      return 'Heavy snowfall';
    default:
      return 'Unknown';
  }
};

export const timeSeriesToWeather = (timeSeries: TimeSeries): Weather => {
  const { symbol_code, air_temperature, wind_speed, wind_from_direction } =
    timeSeries.data;

  if (
    symbol_code === undefined ||
    air_temperature === undefined ||
    wind_speed === undefined ||
    wind_from_direction === undefined
  ) {
    throw new Error('missing parameter data, unable to read weather data');
  }

  return {
    time: DateTime.fromISO(timeSeries.time),
    symbol: parseWeatherSymbol(symbol_code),
    temperature: Math.round(air_temperature),
    windSpeed: Math.round(wind_speed),
    windDirection: wind_from_direction,
  };
};
